import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type { ICampaign } from '../../../redux/buzzbridge/types';
import { SectionLabel } from '../BrandCampaignDetailPage';

import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_HIGHEST,
} from '../brandDashboardTokens';
import { DeliverableBulletList } from '../../../components/BuzzBridge/DeliverableBulletList';

interface BulletListProps {
    items: string[];
    icon: React.ReactNode;
    accentColor?: string;
}

function BulletList({ items, icon, accentColor = BRAND_LUXE_PRIMARY }: BulletListProps) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {items.map((item, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ color: accentColor, flexShrink: 0, mt: '2px' }}>{icon}</Box>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 14,
                            color: 'rgba(255,255,255,0.78)',
                            lineHeight: 1.6,
                        }}>
                        {item}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

export function CampaignDetailSections({ campaign }: { campaign: ICampaign }) {
    const hasObjectives = campaign.objectives && campaign.objectives.length > 0;
    const hasRequirements = campaign.requirements && campaign.requirements.length > 0;
    const hasDeliverables = campaign.deliverables && campaign.deliverables.length > 0;
    const hasProducts = campaign.products && campaign.products.length > 0;

    if (!hasObjectives && !hasRequirements && !hasDeliverables && !hasProducts) return null;

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {hasObjectives && (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                    <SectionLabel>Objectives</SectionLabel>
                    <BulletList items={campaign.objectives!} icon={<FlagOutlinedIcon sx={{ fontSize: 16 }} />} />
                </Box>
            )}

            {hasRequirements && (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                    <SectionLabel>Requirements</SectionLabel>
                    <BulletList
                        items={campaign.requirements!}
                        icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                        accentColor={BRAND_LUXE_SECONDARY}
                    />
                </Box>
            )}

            {hasDeliverables && (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                    <SectionLabel>Deliverables</SectionLabel>
                    <DeliverableBulletList deliverables={campaign.deliverables!} />
                </Box>
            )}

            {hasProducts && (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                    <SectionLabel>Products</SectionLabel>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {campaign.products!.map((product, i) => (
                            <Chip
                                key={i}
                                icon={
                                    <LocalShippingOutlinedIcon
                                        sx={{ fontSize: 14, color: `${BRAND_LUXE_PRIMARY} !important` }}
                                    />
                                }
                                label={product}
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 13,
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                    color: '#fff',
                                    border: 'none',
                                }}
                            />
                        ))}
                    </Box>
                    {campaign.physical_product && (
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: BRAND_LUXE_SECONDARY,
                                mt: 1.5,
                            }}>
                            Physical product shipment included
                        </Typography>
                    )}
                </Box>
            )}
            {campaign.exclusive_rights ? (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                    <SectionLabel>Exclusive rights</SectionLabel>
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: '#fff',
                            lineHeight: 1.55,
                        }}>
                        Full commercial usage for 12 months. Creators grant the brand exclusive commercial rights to
                        submitted content for this engagement.
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 12,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: 'rgba(255,255,255,0.55)',
                            mt: 1.5,
                            lineHeight: 1.5,
                        }}>
                        By applying, creators accept the terms of this engagement.
                    </Typography>
                </Box>
            ) : null}
        </Box>
    );
}
